/**
 * Fase 2B — DocumentLifecycleActions
 *
 * Context-afhankelijke actiemenu's + de 3 kern-dialogen:
 *   - NewVersionDialog
 *   - MarkAsDuplicateDialog
 *   - CancelDocumentDialog
 *
 * RBAC (client-side spiegel van backend):
 *   Nieuwe versie maken:    Owner/Admin/Manager/Medewerker
 *   Activeren:              Owner/Admin/Manager
 *   Markeren als duplicaat: Owner/Admin/Manager/Medewerker
 *   Annuleren:              Owner/Admin/Manager
 *   Restore:                Owner/Admin
 *   Hard delete concept:    Owner/Admin
 *
 * Backend valideert opnieuw; UI verbergt alleen ongebruikte knoppen zodat er
 * geen 403-toast verschijnt.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
  MoreVertical, GitBranch, Copy, XCircle, RefreshCcw, Trash2, Play,
  Loader2,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

// ────────────────────────────────────────────────────────────
// Utility — dialog voor eenvoudige reden-vraag + confirm actie
// ────────────────────────────────────────────────────────────
function ReasonDialog({
  open, onOpenChange, title, description, actionLabel,
  actionClass = "bg-[#0A192F] hover:bg-[#0055FF] text-white",
  reasonRequired = true,
  onSubmit,
  testId,
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleSubmit = async () => {
    if (reasonRequired && !reason.trim()) {
      toast.error("Reden is verplicht.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(reason.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-none" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-chivo text-[#0A192F]">{title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>
        {reasonRequired && (
          <div>
            <Label className="text-xs">Reden (verplicht)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-none mt-1"
              data-testid={`${testId}-reason`}
            />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            disabled={busy}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || (reasonRequired && !reason.trim())}
            className={`rounded-none ${actionClass}`}
            data-testid={`${testId}-confirm`}
          >
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// Actiemenu — statusafhankelijk + RBAC-afhankelijk
// ────────────────────────────────────────────────────────────
export default function DocumentLifecycleActions({
  docType, docId, lifecycleStatus, onChanged,
}) {
  const { user } = useAuth();
  const role = user?.role || "";
  const isAdmin = ["owner", "admin"].includes(role);
  const isManager = ["owner", "admin", "manager"].includes(role);
  const anyRole = ["owner", "admin", "manager", "medewerker"].includes(role);

  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = async (endpoint, body, successMsg) => {
    setBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/lifecycle/${docType}/${docId}/${endpoint}`,
        body || {},
      );
      toast.success(successMsg || "Actie voltooid.");
      onChanged?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteConcept = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/admin/lifecycle/${docType}/${docId}`);
      toast.success("Concept-document verwijderd.");
      onChanged?.({ deleted: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Bepaal welke acties beschikbaar zijn per status
  const canNewVersion = lifecycleStatus === "actief" && anyRole;
  const canActivate = lifecycleStatus === "concept" && isManager;
  const canEdit = lifecycleStatus === "concept" && anyRole;
  const canMarkDuplicate = ["actief", "concept"].includes(lifecycleStatus) && anyRole;
  const canCancel = ["actief", "concept", "duplicaat"].includes(lifecycleStatus) && isManager;
  const canRestore = ["duplicaat", "geannuleerd"].includes(lifecycleStatus) && isAdmin;
  const canDelete = lifecycleStatus === "concept" && isAdmin;

  // Als geen enkele actie beschikbaar (bv. medewerker + geannuleerd) — verberg
  const anyAction = canNewVersion || canActivate || canEdit || canMarkDuplicate
    || canCancel || canRestore || canDelete;
  if (!anyAction) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-8"
            disabled={busy}
            data-testid={`lifecycle-actions-${docId}`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <MoreVertical className="w-4 h-4" />
            )}
            <span className="ml-1 text-xs">Acties</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-none">
          {canNewVersion && (
            <DropdownMenuItem
              onClick={() => setDialog("new_version")}
              data-testid={`lifecycle-action-new-version-${docId}`}
            >
              <GitBranch className="w-4 h-4 mr-2" /> Nieuwe versie maken
            </DropdownMenuItem>
          )}
          {canActivate && (
            <DropdownMenuItem
              onClick={() => call("activate", {}, "Nieuwe versie is nu actief. Oude versie is op 'vervangen' gezet.")}
              data-testid={`lifecycle-action-activate-${docId}`}
            >
              <Play className="w-4 h-4 mr-2" /> Activeren
            </DropdownMenuItem>
          )}
          {canMarkDuplicate && (
            <DropdownMenuItem
              onClick={() => setDialog("mark_duplicate")}
              data-testid={`lifecycle-action-mark-duplicate-${docId}`}
            >
              <Copy className="w-4 h-4 mr-2" /> Markeren als duplicaat
            </DropdownMenuItem>
          )}
          {canCancel && (
            <DropdownMenuItem
              onClick={() => setDialog("cancel")}
              data-testid={`lifecycle-action-cancel-${docId}`}
            >
              <XCircle className="w-4 h-4 mr-2" /> Document annuleren
            </DropdownMenuItem>
          )}
          {canRestore && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDialog("restore")}
                data-testid={`lifecycle-action-restore-${docId}`}
              >
                <RefreshCcw className="w-4 h-4 mr-2" /> Herstellen naar actief
              </DropdownMenuItem>
            </>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDialog("delete")}
                className="text-rose-700 focus:text-rose-700"
                data-testid={`lifecycle-action-delete-${docId}`}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Concept verwijderen
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Nieuwe versie: geen reden verplicht */}
      <ReasonDialog
        open={dialog === "new_version"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Nieuwe versie maken"
        description="Er wordt een nieuwe concept-versie gemaakt op basis van dit document. De huidige versie blijft ACTIEF totdat u de nieuwe versie activeert. Pas dan wordt de huidige op 'vervangen' gezet."
        actionLabel="Concept-versie aanmaken"
        actionClass="bg-[#0055FF] hover:bg-[#0A192F] text-white"
        reasonRequired={false}
        onSubmit={() => call("new-version", {}, "Concept-versie aangemaakt. Bewerk en activeer wanneer klaar.")}
        testId="lifecycle-dialog-new-version"
      />

      {/* Markeer als duplicaat: reden verplicht */}
      <ReasonDialog
        open={dialog === "mark_duplicate"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Markeren als duplicaat"
        description="Gebruik dit voor per ongeluk dubbel aangemaakte documenten (bv.: dubbel ingevuld door klant). Het document wordt niet meer gebruikt voor documentenset, ZIP of e-mail. Het blijft zichtbaar in de historie."
        actionLabel="Markeer als duplicaat"
        actionClass="bg-amber-600 hover:bg-amber-700 text-white"
        reasonRequired={true}
        onSubmit={(reason) => call("mark-duplicate", { reason }, "Document is gemarkeerd als duplicaat.")}
        testId="lifecycle-dialog-mark-duplicate"
      />

      {/* Annuleren: reden verplicht */}
      <ReasonDialog
        open={dialog === "cancel"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Document annuleren"
        description="Het document wordt ongeldig verklaard (verkeerde klant, foutieve zending, proces gestopt). Wordt niet meer gebruikt in actieve processen. Blijft in historie."
        actionLabel="Document annuleren"
        actionClass="bg-rose-600 hover:bg-rose-700 text-white"
        reasonRequired={true}
        onSubmit={(reason) => call("cancel", { reason }, "Document is geannuleerd.")}
        testId="lifecycle-dialog-cancel"
      />

      {/* Restore: reden verplicht */}
      <ReasonDialog
        open={dialog === "restore"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Herstellen naar actief"
        description="Het document wordt teruggezet op 'actief'. Dit is alleen mogelijk wanneer er geen andere actieve versie in dezelfde context bestaat."
        actionLabel="Herstellen naar actief"
        actionClass="bg-emerald-600 hover:bg-emerald-700 text-white"
        reasonRequired={true}
        onSubmit={(reason) => call("restore", { reason }, "Document is hersteld naar actief.")}
        testId="lifecycle-dialog-restore"
      />

      {/* Hard delete concept */}
      <ReasonDialog
        open={dialog === "delete"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Concept-document verwijderen"
        description="Deze actie is definitief. Het concept-document wordt uit de database verwijderd. Alleen mogelijk als het concept nooit verzonden of gekoppeld is aan een documentenset."
        actionLabel="Definitief verwijderen"
        actionClass="bg-rose-700 hover:bg-rose-800 text-white"
        reasonRequired={false}
        onSubmit={deleteConcept}
        testId="lifecycle-dialog-delete"
      />
    </>
  );
}
